import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useParams, Link } from 'react-router-dom';
import { useFetch } from '../hooks/useApiCall';
import { getAppointmentDetail, cancelAppointment } from '../services/appointmentService';
import { getErrorMessage } from '../utils/errorHandler';
import { PmLoading, PmError, PmEmpty, PmNotice } from '../components/pm/PmStates';
import { IconArrow, IconCalendar, IconClock, IconPin, IconWhatsapp } from '../components/pm/PmIcons';
import { StatusChip, formatDate, formatTime } from './MyAppointments';
import { BUSINESS, ROUTES, WHATSAPP } from '../data/pmContent';

const AppointmentDetail = () => {
  const { id } = useParams();
  const { data: appointment, loading, error, refetch } = useFetch(getAppointmentDetail, id);

  const [cancelling, setCancelling] = useState(false);
  const [notice, setNotice] = useState(null); // { variant, message }

  const handleCancel = async () => {
    if (!window.confirm('¿Está segura que desea cancelar esta cita?')) return;

    try {
      setCancelling(true);
      await cancelAppointment(id);
      setNotice({ variant: 'success', message: 'La cita ha sido cancelada correctamente.' });
      refetch();
    } catch (err) {
      setNotice({ variant: 'danger', message: getErrorMessage(err) });
    } finally {
      setCancelling(false);
    }
  };

  const BackLink = () => (
    <Link to="/mis-citas" className="back-link">
      <IconArrow style={{ transform: 'rotate(180deg)' }} /> Volver a mis citas
    </Link>
  );

  let body;
  if (loading) {
    body = <PmLoading message="Cargando la cita…" />;
  } else if (error) {
    body = <PmError message={error} onRetry={refetch} />;
  } else if (!appointment) {
    body = <PmEmpty title="Cita no encontrada" text="Es posible que el enlace no sea válido o la cita ya no exista." />;
  } else {
    const canCancel = ['pending', 'confirmed'].includes(appointment.status);
    // Support both plural (new) and singular (legacy)
    const services = appointment.services || (appointment.service ? [appointment.service] : []);
    const title = services.length > 1 ? `${services.length} servicios` : (services[0]?.name || 'Cita');
    const total = services.reduce((sum, s) => sum + (Number(s.price) || 0), 0);

    body = (
      <div className="acct-detail">
        <article className="acct-card">
          <div className="acct-detail__title">
            <h1>{title}</h1>
            <StatusChip status={appointment.status} />
          </div>

          <div>
            <span className="acct-label">Servicios</span>
            <ul className="acct-services">
              {services.map((svc, idx) => (
                <li key={svc.id || idx}>
                  <span className="acct-services__name">
                    {svc.name}
                    {svc.employee && <span className="chip chip--soft">{svc.employee.name}</span>}
                  </span>
                  {svc.price && (
                    <span className="acct-services__price">RD$ {Number(svc.price).toLocaleString()}</span>
                  )}
                </li>
              ))}
              {services.length > 1 && total > 0 && (
                <li>
                  <span className="acct-services__name"><strong>Total</strong></span>
                  <span className="acct-services__price">RD$ {total.toLocaleString()}</span>
                </li>
              )}
            </ul>
          </div>

          <div className="acct-facts">
            <div>
              <span className="acct-label">Fecha</span>
              <p>{formatDate(appointment.scheduled_at, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
            </div>
            <div>
              <span className="acct-label">Hora</span>
              <p>{formatTime(appointment.scheduled_at)}</p>
            </div>
            {appointment.notes && (
              <div>
                <span className="acct-label">Notas</span>
                <p>{appointment.notes}</p>
              </div>
            )}
            {appointment.location && (
              <div>
                <span className="acct-label">Ubicación</span>
                <p>{appointment.location}</p>
              </div>
            )}
          </div>

          {canCancel && (
            <div className="acct-card__foot">
              <button
                type="button"
                className="btn btn--danger btn--sm"
                onClick={handleCancel}
                disabled={cancelling}
              >
                {cancelling ? <><span className="pm-spinner" aria-hidden="true" /> Cancelando…</> : 'Cancelar cita'}
              </button>
            </div>
          )}
        </article>

        <aside className="acct-card">
          <h3>Tu visita</h3>
          <ul className="acct-card__meta">
            <li><IconPin /> {BUSINESS.address}</li>
            <li><IconClock /> {BUSINESS.hoursShort}</li>
            <li><IconCalendar /> {BUSINESS.parking}</li>
          </ul>
          <p style={{ fontSize: 'var(--fs-sm)' }}>
            ¿Necesitas cambiar la hora o tienes alguna duda? Escríbenos y lo coordinamos.
          </p>
          <div className="acct-card__foot">
            <a className="btn btn--wa btn--sm" href={WHATSAPP} target="_blank" rel="noopener noreferrer">
              <IconWhatsapp /> WhatsApp
            </a>
            <Link className="btn btn--secondary btn--sm" to={ROUTES.agendar}>Agendar otra cita</Link>
          </div>
        </aside>
      </div>
    );
  }

  return (
    <div className="pm-scope account">
      <Helmet>
        <title>Detalle de cita - {BUSINESS.name}</title>
      </Helmet>

      <section className="section">
        <div className="container container--narrow">
          <BackLink />
          {notice && (
            <PmNotice variant={notice.variant} onClose={() => setNotice(null)}>{notice.message}</PmNotice>
          )}
          {body}
        </div>
      </section>
    </div>
  );
};

export default AppointmentDetail;
